import { nanoid } from "nanoid"
import { jobShopTag, pageTag, tenantTag } from "@/utils/cacheTags"
import { contentModulesCacheItemKey } from "@/utils/cacheKeys"
import { paletteByKey, resolveColorSlots } from "@/utils/colorSlots"
import Logger from "@/utils/Logger"

export async function useContentModules(pageId: string) {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId, acceptLanguage } = useHeaderStore()
  const { dataCacheTtl, dataCacheStaleTtl } = useRuntimeConfig()
  // Read before the first await in this async setup, since it needs the Nuxt instance. The
  // layout awaits useJobShopData() above the page, so the palette is already in the payload
  // by the time the handler below runs.
  const { data: jobShopData } = useNuxtData<JobShop>("jobShopData")

  const requestId = nanoid()
  const contextString = `content modules for pageId: "${pageId}", tenantId: "${tenantId}" and jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const cacheKey = contentModulesCacheItemKey(pageId)
  const {
    data: contentModules,
    status: contentModulesStatus,
    error: contentModulesError,
  } = await useAsyncData(cacheKey, async () => {
    // Stale-if-error (JET-2212): keep last-known-good on upstream unavailability (not 4xx).
    const modules = await useDataCacheWithStale(cacheKey, () => $api.content.getContentModules(tenantId, jobShopId, pageId, acceptLanguage), {
      maxAge: dataCacheTtl,
      staleIfError: dataCacheStaleTtl,
      requestId,
      tags: [tenantTag(tenantId), jobShopTag(jobShopId), pageTag(pageId)],
    })

    // After the cache, never before: the shared entry keeps the slot references so a
    // palette change cannot leave a frozen hex behind it.
    return resolveColorSlots(modules, paletteByKey(jobShopData.value))
  })

  if (import.meta.server) {
    if (!contentModulesError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = contentModulesError.value?.data?.errors?.[0]?.message || contentModulesError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, contentModulesError.value)
    }
  }

  if (contentModulesError.value) {
    throw createError({
      statusCode: contentModulesError.value.statusCode,
      statusMessage: t("error-messages.content-modules-error"),
    })
  }

  return { contentModules, contentModulesStatus, contentModulesError }
}
